import { Button, Card, Descriptions, Divider, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react";
import { BOMAPI, WorkOrderAPI } from "@/apis/manufacturing";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BomStatusDef, WorkOrderStatusDef, BomStatus } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";
import { IconEdit } from "@arco-design/web-react/icon";
import { formatDate } from "@/utils";
import { Link } from "react-router-dom";

export const BillOfMaterialInfo = () => {
    const params = useParams();
    const bomId = params.bomId;
    const navigate = useNavigate();
    const [info, setInfo] = useState({});
    const [woHistory, setWoHistory] = useState([]);

    const getInfo = useCallback(async () => {
        try {
            const values = await BOMAPI.get(bomId);
            setInfo(values);

            const woResult = await WorkOrderAPI.actives({ bomId });
            setWoHistory(woResult || []);
        } catch (error) {
            Message.error(error.message);
        }
    }, [bomId]);

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    const handleEnable = async () => {
        try {
            await BOMAPI.activate(info.id);
            Message.success("BOM enabled successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const handleDisable = async () => {
        try {
            await BOMAPI.deactivate(info.id);
            Message.success("BOM disabled successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    };

    const finishedItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "Qty", dataIndex: "quantity" },
        { title: "UOM", dataIndex: "uom" },
        {
            title: "Value Allocation",
            dataIndex: "valueAllocation",
            render: (val) => val != null ? `${val}%` : ""
        },
    ];

    const rawItemColumns = [
        { title: "Item Name", dataIndex: "itemName" },
        { title: "Description", dataIndex: "description" },
        { title: "Qty", dataIndex: "quantity" },
        { title: "UOM", dataIndex: "uom" },
    ];

    return (
        <Card title={
            <Space>
                <span>{info.bomName}</span>
                <Tag><EnumLabel enumObj={BomStatusDef} value={info.status} /></Tag>
            </Space>
        } extra={
            <Space>
                <Button type="primary" icon={<IconEdit />} onClick={() => navigate(`/workspace/manufacturing/bom/${bomId}/edit`)}>Edit</Button>
                {info.status === BomStatus.Inactive && (
                    <Popconfirm title="Are you sure you want to enable this BOM?" onOk={handleEnable}>
                        <Button type="primary" status="success">Enable</Button>
                    </Popconfirm>
                )}
                {info.status === BomStatus.Active && (
                    <Popconfirm title="Are you sure you want to disable this BOM?" onOk={handleDisable}>
                        <Button type="primary" status="danger">Disable</Button>
                    </Popconfirm>
                )}
                <Button onClick={() => navigate("/workspace/manufacturing/bom")}>Back</Button>
            </Space>
        }>
            <Descriptions colon=":" column={4} data={[
                { label: "BOM Name", value: info.bomName },
                { label: "Description", value: info.description },
                { label: "Waste Factor", value: info.wasteFactor != null ? `${info.wasteFactor}%` : "" },
                { label: "Date Created", value: info.createTime ? formatDate(info.createTime) : "" },
                { label: "Notes", value: info.notes, span: 3 },
            ]} />

            <div style={{ marginTop: 24, marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Finished Items</span>
            </div>
            <Table rowKey="id" columns={finishedItemColumns} data={info.finishedItems || []} pagination={false} />

            <div style={{ marginTop: 24, marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Raw Items</span>
            </div>
            <Table rowKey="id" columns={rawItemColumns} data={info.rawItems || []} pagination={false} />

            <Divider />

            <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>WO History</span>
            </div>
            <Table rowKey="id" columns={[
                {
                    title: "WO#",
                    dataIndex: "workOrderNo",
                    render: (workOrderNo, record) => (
                        <Link to={`/workspace/manufacturing/work-order/${record.id}`}>{workOrderNo}</Link>
                    ),
                },
                { title: "WO Qty", dataIndex: "quantity" },
                {
                    title: "Status",
                    dataIndex: "status",
                    render: (status) => <EnumLabel enumObj={WorkOrderStatusDef} value={status} />,
                },
            ]} data={woHistory} pagination={false} />
        </Card>
    );
};
